/**
 * ComposeExplainer — progressive-disclosure explainer for COMPOSE (C).
 *
 * COMPOSE neutralizes the vulnerability class "safe components compose into
 * an unsafe whole" by modeling capability composition as an operad algebra
 * whose carrier is the set of safe terms. Operadic substitution is closed
 * on the safe carrier, so an unsafe composite cannot be injected — it is
 * structurally inexpressible (INERT mode: composition may run, but the
 * algebra forces a safe result).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { COMPOSE_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { OperadicTree } from "../categorical/operad/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR.COMPOSE;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-compose",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "COMPOSE",
  subtitle: "C · Operad · INERT · G1 Authority",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Operad (structure)", href: "#explainer-operad", accent: ACCENT },
  { label: "Cooperad (DECOMPOSE)", href: "#explainer-cooperad" },
  { label: "PERMIT (G1 sibling)", href: "#explainer-permit", accent: THEOREM_COLOR.PERMIT },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>COMPOSE</strong> says: plug safe parts together and the whole is safe, by
      construction. There is no way to assemble an unsafe composite out of safe pieces.
    </p>
  ),
  intuition: (
    <>
      <p>
        A subtle class of bug is emergent: each component is fine in isolation, but wiring them
        together opens a hole — a gadget chain, a deserialization sink reached only via a safe
        adapter. Auditing each part separately never sees it.
      </p>
      <p>
        CSK models capability composition as an operad whose algebra carrier is the set of{" "}
        <em>safe</em> terms. Operadic substitution — plugging an operation into an argument slot —
        lands back in the safe carrier by the algebra structure map. The unsafe composite is not
        produced and then caught; it is unconstructable in the source.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`\Omega`}</MathInline> be the binary safety operad and{" "}
        <MathInline>{tex`A`}</MathInline> a <code>SafeO-Algebra</code> for it: a carrier{" "}
        <MathInline>{tex`|A|`}</MathInline> of safe terms with a structure map{" "}
        <MathInline>{tex`\gamma : \Omega(n) \otimes |A|^{n} \to |A|`}</MathInline> for each arity{" "}
        <MathInline>{tex`n`}</MathInline>, satisfying the operad associativity and unit laws.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        Because <MathInline>{tex`\gamma`}</MathInline> is total on safe arguments and the operad
        laws make substitution associative, the closure of safe terms under composition is itself
        safe: <MathInline>{tex`\text{safe-in} \Rightarrow \text{safe-out}`}</MathInline>. Injecting
        an unsafe argument is not a morphism into <MathInline>{tex`|A|`}</MathInline>.{" "}
        <code>COMPOSE</code> is a projection of the Categorical Immunity Theorem and pairs with the{" "}
        <code>DECOMPOSE</code> SDP (operad <MathInline>{tex`\dashv`}</MathInline> cooperad).
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> unsafe composition of otherwise-safe
        capabilities — deserialization gadget chains, prototype pollution via safe adapters
        (CWE-502, CWE-915, CWE-1321; e.g., CVE-2021-23337). Realized by{" "}
        <em>structural inexpressibility</em> of the unsafe composite; the mode is{" "}
        <strong>INERT</strong> — composition may run, but the algebra forces a safe result.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you compose through a raw host combinator
        outside the safety operad, you have left the kernel. COMPOSE is then vacuous on that
        composite — the closure has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {COMPOSE_META.seamType}. <strong>Bridge</strong>: safety waiver (governance artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "COMPOSE theorem card",
      path: "docs/reference/theorem-cards/COMPOSE.md",
    },
    {
      label: "Kernel module (main theorem COMPOSE-neutralized)",
      path: `agda/${COMPOSE_META.agdaSource}`,
      note: `COMPOSE_META.agdaSource is "${COMPOSE_META.agdaSource}"; the repo path is prefixed with "agda/". COMPOSE-neutralized at line 102 (module lines 76–236).`,
    },
    {
      label: "DECOMPOSE compositionality module",
      path: "agda/Kernel/CompositionalityPrinciple/DECOMPOSE.agda",
    },
    {
      label: "Theorem Canon (§4.2 INERT; §7 DECOMPOSE SDP)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="COMPOSE"
      code="C"
      structure="Operad"
      mode="inert"
      generator="G1"
      seamType={COMPOSE_META.seamType}
      bridgeArtifact="safety waiver"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <OperadicTree
        accent={ACCENT}
        title="Safe operad operation f ∈ Ω(3) substituting safe arguments"
      />
    </div>
  </>
);

export function ComposeExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
